import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useTrackers } from "@/hooks/useTrackers";
import { useMonitoringFrequencies } from "@/hooks/useFrequencies";
import { useMonitoringPlan } from "@/hooks/useMonitoringPlan";
import { Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { DismissibleChip } from "@/components/consultation/shared/DismissibleChip";
import type { Tracker, TrackerCategory } from "@/types/monitoring";
import { useDebounce } from "@/hooks/use-debounce";

interface AddMonitoringAdviceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointmentId: string;
  patientId: string;
}

const CATEGORIES: {
  id: TrackerCategory | "all";
  label: string;
}[] = [
  { id: "all", label: "All" },
  { id: "glucose", label: "Glucose" },
  { id: "vital", label: "Vitals" },
  { id: "body_measurements", label: "Body Measurements" },
];

export const AddMonitoringAdviceDialog = ({
  open,
  onOpenChange,
  appointmentId,
  patientId,
}: AddMonitoringAdviceDialogProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<
    TrackerCategory | "all"
  >("all");
  const [pendingTracker, setPendingTracker] = useState<Tracker | null>(null);
  const [pendingFrequency, setPendingFrequency] = useState<string>("");
  const [pendingAdvice, setPendingAdvice] = useState("");

  const debouncedSearch = useDebounce(searchQuery, 300);

  // Fetch data
  const { data: trackers, isLoading: isLoadingTrackers } = useTrackers({
    search: debouncedSearch,
    category: selectedCategory === "all" ? undefined : selectedCategory,
    appointment_id: appointmentId,
  });

  const { data: frequencies } = useMonitoringFrequencies();
  const { upsertEntry, data: plan } = useMonitoringPlan(patientId);

  // The patient's active plan (shared across appointments and providers)
  const selectedItems = useMemo(() => plan ?? [], [plan]);

  const selectedTrackerIds = useMemo(() => {
    return new Set(selectedItems.map((entry) => entry.tracker.id));
  }, [selectedItems]);

  const handleSelectTracker = (tracker: Tracker) => {
    setPendingTracker(tracker);
    setPendingFrequency("");
    setPendingAdvice("");
  };

  const handleConfirmAdd = async () => {
    if (!pendingTracker) return;
    try {
      await upsertEntry.mutateAsync({
        tracker: pendingTracker.id,
        ...(pendingFrequency ? { frequency: Number(pendingFrequency) } : {}),
        advice: pendingAdvice.trim() || undefined,
        appointment_id: appointmentId,
      });
      setPendingTracker(null);
    } catch (error) {
      console.error("Failed to add monitoring advice:", error);
      toast.error(
        `Couldn't add ${pendingTracker.name}. Check the list before trying again.`
      );
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      setSearchQuery("");
      setSelectedCategory("all");
    }, 300);
  };

  const filteredTrackers = useMemo(() => {
    if (!trackers) return [];
    // Exclude already-selected trackers from the list
    return trackers.filter((t) => !selectedTrackerIds.has(t.id));
  }, [trackers, selectedTrackerIds]);

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[600px] h-[600px] flex flex-col p-0 gap-0 overflow-hidden border-border/60">
          <DialogHeader className="p-6 pb-0 shrink-0">
            <DialogTitle className="z-h2 text-foreground">
              Add Monitoring Advice
            </DialogTitle>
          </DialogHeader>
          <Separator className="mt-3" />

          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            {/* Already Added Section — only shown when items exist */}
            {selectedItems.length > 0 && (
              <div className="px-6 py-4 border-b border-border/60 shrink-0">
                <h3 className="z-label text-muted-foreground mb-3">
                  Already Added
                </h3>
                <div className="rounded-xl border border-border/60 p-3 max-h-[120px] overflow-y-auto scrollbar-thin">
                  <div className="flex flex-wrap gap-2">
                    {selectedItems.map((entry) => (
                      // Read-only: stopping lives in the card's row menu,
                      // behind a confirmation.
                      <DismissibleChip
                        key={entry.id}
                        label={entry.tracker.name}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Search + Tabs + Results */}
            <div className="flex-1 flex flex-col min-h-0 bg-background">
              {/* Search input — placed above the segmentation tabs */}
              <div className="px-6 pt-4 pb-3 shrink-0">
                <label className="z-label text-muted-foreground mb-2 block">
                  Search Item
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search trackers..."
                    className="pl-9"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    autoFocus
                  />
                  {isLoadingTrackers && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
                  )}
                </div>
              </div>

              {/* Segmentation tabs */}
              <div className="px-6 py-3 shrink-0">
                <Tabs
                  value={selectedCategory}
                  onValueChange={(val) =>
                    setSelectedCategory(val as TrackerCategory | "all")
                  }
                >
                  <TabsList className="h-9">
                    {CATEGORIES.map((cat) => (
                      <TabsTrigger key={cat.id} value={cat.id}>
                        {cat.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              </div>

              {/* Results */}
              <div className="flex-1 overflow-y-auto scrollbar-thin p-6">
                {isLoadingTrackers ? (
                  <div className="flex items-center justify-center py-8 text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    Loading trackers...
                  </div>
                ) : filteredTrackers.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {filteredTrackers.map((tracker) => (
                      <DismissibleChip
                        key={tracker.id}
                        label={tracker.name}
                        onAdd={() => handleSelectTracker(tracker)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground z-body">
                    {searchQuery.length > 0
                      ? `No trackers found matching "${searchQuery}"`
                      : "No more trackers available"}
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tracker details sub-dialog */}
      <Dialog
        open={!!pendingTracker}
        onOpenChange={(open) => {
          if (!open) setPendingTracker(null);
        }}
      >
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="z-h2">{pendingTracker?.name}</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-4 py-2">
            <div className="flex flex-col gap-1.5">
              <Label>Frequency</Label>
              <Select
                value={pendingFrequency}
                onValueChange={setPendingFrequency}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select frequency" />
                </SelectTrigger>
                <SelectContent>
                  {frequencies?.map((f) => (
                    <SelectItem key={f.id} value={String(f.id)}>
                      {f.display_text}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>
                Advice{" "}
                <span className="z-hint text-muted-foreground">(optional)</span>
              </Label>
              <Textarea
                placeholder="Add any advice for this tracker..."
                value={pendingAdvice}
                onChange={(e) => setPendingAdvice(e.target.value)}
                className="resize-none"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingTracker(null)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmAdd} disabled={upsertEntry.isPending}>
              {upsertEntry.isPending && (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              )}
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
